import type { ValueChangeItem } from "@/profitability/records/value-changes";
import type { FindingActivity, FindingStatus } from "./types";

const text = (value: unknown) => (typeof value === "string" ? value : null);

const numberOf = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const isStatus = (value: unknown): value is FindingStatus =>
  value === "open" ||
  value === "investigating" ||
  value === "action_required" ||
  value === "explained" ||
  value === "resolved";

/**
 * The fields an event already stored as a before and an after. A sentence
 * stays the event; these are the rows under it.
 */
export const activityChanges = (
  activity: FindingActivity,
): ValueChangeItem[] => {
  const { data } = activity;
  const changes: ValueChangeItem[] = [];
  const previousRule = text(data.previous_rule);
  const rule = text(data.rule);
  if (previousRule && rule && previousRule !== rule)
    changes.push({
      label: "Rule",
      from: { kind: "text", text: previousRule },
      to: { kind: "text", text: rule },
    });

  const previousShortfall = numberOf(data.previous_shortfall);
  const shortfall = numberOf(data.shortfall);
  if (
    previousShortfall !== null &&
    shortfall !== null &&
    previousShortfall !== shortfall
  )
    changes.push({
      label: "Shortfall",
      from: { kind: "money", amount: previousShortfall },
      to: { kind: "money", amount: shortfall, loss: shortfall < 0 },
    });

  const previousFlag = text(data.previous_flag);
  const flag = text(data.flag);
  if (previousFlag && flag && previousFlag !== flag)
    changes.push({
      label: "Flag",
      from: { kind: "text", text: previousFlag },
      to: { kind: "text", text: flag },
    });

  const previousStatus = data.previous_status;
  const status = data.status;
  if (isStatus(previousStatus) && isStatus(status) && previousStatus !== status)
    changes.push({
      label: "Status",
      from: { kind: "status", status: previousStatus },
      to: { kind: "status", status },
    });

  return changes;
};
